import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    styled,
    Stack,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import { z } from 'zod';
import { IBeginWorkOnSuccessArgs } from 'src/types';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Title from 'src/components/Title';

import { UserIcon } from 'src/icons/UserIcon';
import { TgArrowIcon } from 'src/icons/TgArrowIcon';
import PhoneIcon from 'src/icons/Phone';
import UserSevice from 'src/services/UserSevice';
import useUserStore from 'src/stores/user';
import CustomInputField from 'src/components/CustomInputField';
import PhoneMaskField from 'src/components/LoginField/PhoneMaskField';
import TelegramOnboardingConnect from './TelegramOnboardingConnect';

import type { IUserDescription, IUserResponse, LeadDeliveryChannel } from 'src/types/User';

const getPhoneDigits = (value?: string) => (value || '').replace(/\D/g, '');

const BUSINESS_DESCRIPTION_MAX_CHARS = 300;

const USER_ROLE_OTHER_VALUE = 'other';

const USER_ROLE_OPTIONS = [
    'Предприниматель / собственник бизнеса',
    'Отдел продаж',
    'Отдел маркетинга',
    'Лидогенератор',
    'Агентство',
    'Фрилансер / эксперт',
    'HR / рекрутер',
    'Другое',
].map((label) => ({
    label,
    value: label === 'Другое' ? USER_ROLE_OTHER_VALUE : label,
}));

const LEGACY_USER_ROLE_MAP: Record<string, string> = {
    advertising_agency: 'Агентство',
    business_owner: 'Предприниматель / собственник бизнеса',
    freelancer: 'Фрилансер / эксперт',
    hr: 'HR / рекрутер',
    marketer: 'Отдел маркетинга',
    professional: 'Фрилансер / эксперт',
};

const LEAD_DELIVERY_CHANNEL_VALUES = ['telegram', 'email', 'crm', 'other'] as const;

const LEAD_DELIVERY_OPTIONS: Array<{ value: LeadDeliveryChannel; label: string }> = [
    { value: 'telegram', label: 'Telegram' },
    { value: 'email', label: 'Email' },
    { value: 'crm', label: 'CRM' },
    { value: 'other', label: 'Другое' },
];

const getLeadDeliveryChannelsDefault = (channels?: LeadDeliveryChannel[] | null) => {
    return Array.isArray(channels) ? channels.filter((channel) => LEAD_DELIVERY_CHANNEL_VALUES.includes(channel)) : [];
};

const isTextFilled = (value?: string | null) => Boolean((value || '').trim());

const getUserRoleFormDefaults = (role?: string) => {
    const normalizedRole = role ? LEGACY_USER_ROLE_MAP[role] || role : '';
    const isKnownRole = USER_ROLE_OPTIONS.some(({ value }) => value === normalizedRole);

    if (!normalizedRole || isKnownRole) {
        return {
            user_role: normalizedRole,
            custom_role: '',
        };
    }

    return {
        user_role: USER_ROLE_OTHER_VALUE,
        custom_role: normalizedRole,
    };
};

const ProfileSchema = z
    .object({
        first_name: z.string().optional(),
        tg_account: z.string().optional(),
        business_description: z
            .string()
            .min(1, 'Опишите, что вы продаете и кому')
            .max(BUSINESS_DESCRIPTION_MAX_CHARS, `Не более ${BUSINESS_DESCRIPTION_MAX_CHARS} символов`),
        user_role: z.string().min(1, 'Выберите вашу деятельность'),
        custom_role: z.string().max(40, 'Не более 40 символов').optional(),
        website_url: z.string().optional(),
        phone: z
            .string()
            .optional()
            .refine((value) => {
                const digits = getPhoneDigits(value);
                return digits.length === 0 || (digits.length === 11 && digits.startsWith('7'));
            }, 'Введите номер полностью'),
        crm_system: z.string().max(100, 'Не более 100 символов').optional(),
        lead_delivery_channels: z.array(z.enum(LEAD_DELIVERY_CHANNEL_VALUES)).min(1, 'Выберите хотя бы один способ'),
        lead_delivery_other: z.string().max(150, 'Не более 150 символов').optional(),
    })
    .refine(
        (data) => {
            if (data.user_role === USER_ROLE_OTHER_VALUE) {
                return data.custom_role && data.custom_role.trim().length > 0;
            }
            return true;
        },
        {
            message: 'Введите вашу деятельность',
            path: ['custom_role'],
        }
    )
    .refine(
        (data) => {
            if (data.lead_delivery_channels.includes('crm')) {
                return data.crm_system && data.crm_system.trim().length > 0;
            }
            return true;
        },
        {
            message: 'Укажите CRM',
            path: ['crm_system'],
        }
    )
    .refine(
        (data) => {
            if (data.lead_delivery_channels.includes('other')) {
                return data.lead_delivery_other && data.lead_delivery_other.trim().length > 0;
            }
            return true;
        },
        {
            message: 'Укажите способ доставки',
            path: ['lead_delivery_other'],
        }
    )
    .refine(
        (data) => {
            return isTextFilled(data.tg_account) || getPhoneDigits(data.phone).length === 11;
        },
        {
            message: 'Укажите Telegram или номер телефона',
            path: ['tg_account'],
        }
    );

type QuestionnaireFormValues = z.infer<typeof ProfileSchema>;

type QuestionnaireDefaultsSource = Pick<
    IUserResponse,
    | 'first_name'
    | 'tg_account'
    | 'business_description'
    | 'user_role'
    | 'website_url'
    | 'phone'
    | 'crm_system'
    | 'lead_delivery_channels'
    | 'lead_delivery_other'
>;

const getFormDefaultValues = (user: Partial<QuestionnaireDefaultsSource>): QuestionnaireFormValues => {
    const userRoleDefaults = getUserRoleFormDefaults(user.user_role);

    return {
        first_name: user.first_name || '',
        tg_account: user.tg_account || '',
        business_description: user.business_description || '',
        user_role: userRoleDefaults.user_role,
        custom_role: userRoleDefaults.custom_role,
        website_url: user.website_url || '',
        phone: user.phone || '',
        crm_system: user.crm_system || '',
        lead_delivery_channels: getLeadDeliveryChannelsDefault(user.lead_delivery_channels),
        lead_delivery_other: user.lead_delivery_other || '',
    };
};

export interface QuestionnaireProgressItem {
    id: string;
    label: string;
    completed: boolean;
}

export interface QuestionnaireProgressState {
    items: QuestionnaireProgressItem[];
    completed: number;
    total: number;
    percent: number;
    isComplete: boolean;
}

export const getQuestionnaireProgressState = (
    values: Partial<QuestionnaireFormValues> = {}
): QuestionnaireProgressState => {
    const selectedDeliveryChannels = values.lead_delivery_channels || [];
    const isRoleComplete =
        values.user_role === USER_ROLE_OTHER_VALUE ? isTextFilled(values.custom_role) : isTextFilled(values.user_role);
    const isDeliveryComplete =
        selectedDeliveryChannels.length > 0 &&
        (!selectedDeliveryChannels.includes('crm') || isTextFilled(values.crm_system)) &&
        (!selectedDeliveryChannels.includes('other') || isTextFilled(values.lead_delivery_other));

    const items: QuestionnaireProgressItem[] = [
        {
            id: 'user_role',
            label: 'Деятельность',
            completed: isRoleComplete,
        },
        {
            id: 'business_description',
            label: 'Описание бизнеса',
            completed: isTextFilled(values.business_description),
        },
        {
            id: 'lead_delivery_channels',
            label: 'Доставка лидов',
            completed: isDeliveryComplete,
        },
        {
            id: 'contact',
            label: 'Контакт',
            completed: isTextFilled(values.tg_account) || getPhoneDigits(values.phone).length === 11,
        },
    ];
    const completed = items.filter((item) => item.completed).length;
    const total = items.length;

    return {
        items,
        completed,
        total,
        percent: Math.round((completed / total) * 100),
        isComplete: completed === total,
    };
};

const CustomTextField = styled(TextField)(({ theme }) => ({
    '&.MuiTextField-root': { borderRadius: '16px' },
    backgroundColor: 'var(--lm-surface)',
    // --- Новый стиль для value вне фокуса ---
    '& .MuiInputBase-root:not(.Mui-focused) .MuiInputBase-input:not(:placeholder-shown)': {
        color: theme.palette.text.primary,
        opacity: 1,
    },
    // --- Стили для обводки активного инпута ---
    '& .MuiOutlinedInput-root': {
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--lm-text)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--lm-text)',
        },
    },
}));

export const MaxChars = ({
    isError,
    charCount,
    maxChars,
}: {
    isError: boolean;
    charCount: number;
    maxChars: number;
}) => {
    return (
        <Stack
            gap="2px"
            direction="row"
            alignItems="center"
            sx={{ mt: 0.5 }}
        >
            <Typography
                variant="body2"
                sx={{
                    color: isError ? 'error.main' : 'text.secondary',
                }}
            >
                {isError ? `Превышено количество символов (максимум ${maxChars})` : `${charCount}/${maxChars}`}
            </Typography>
        </Stack>
    );
};

export interface IChatsAndChannelsScreenProps {
    onSuccess?: ({ skiped }: IBeginWorkOnSuccessArgs) => void;
    onQuestionnaireProgressChange?: (progress: QuestionnaireProgressState) => void;
    showTelegramOnboarding?: boolean;
}

export default function DescribeYourBusiness({
    onSuccess,
    onQuestionnaireProgressChange,
    showTelegramOnboarding = false,
}: IChatsAndChannelsScreenProps) {
    const { enqueueSnackbar } = useSnackbar();
    const userInfo = useUserStore((state) => ({
        first_name: state.first_name,
        tg_account: state.tg_account,
        business_description: state.business_description,
        user_role: state.user_role,
        phone: state.phone || '',
        isOnboardingCompleted: state.isOnboardingCompleted,
        tg_notification_mode: state.tg_notification_mode,
        onboarding_step: String(state.onboardingStep),
        is_onboarding_completed: state.isOnboardingCompleted,
        is_email_confirmed: state.isEmailConfirmed,
        bonus_points: state.bonusPoints,
        current_subscription: state.current_subscription,
        email: state.email,
        id: state.id,
        username: state.username,
        role: state.role,
        website_url: state.website_url,
        crm_system: state.crm_system || '',
        lead_delivery_channels: state.lead_delivery_channels || [],
        lead_delivery_other: state.lead_delivery_other || '',
        tg_notifications_enabled: state.tg_notifications_enabled,
        is_telegram_connected: state.is_telegram_connected,
    }));

    const defaultFormValues = useMemo(
        () => getFormDefaultValues(userInfo),
        [
            userInfo.first_name,
            userInfo.tg_account,
            userInfo.business_description,
            userInfo.user_role,
            userInfo.website_url,
            userInfo.phone,
            userInfo.crm_system,
            userInfo.lead_delivery_channels,
            userInfo.lead_delivery_other,
        ]
    );

    const {
        control,
        handleSubmit,
        formState: { isValid, errors, isDirty },
        watch,
        trigger,
        clearErrors,
        reset,
    } = useForm<QuestionnaireFormValues>({
        defaultValues: defaultFormValues,
        resolver: zodResolver(ProfileSchema),
        mode: 'onChange',
        resetOptions: {
            keepIsSubmitted: true,
        },
    });

    const formValues = watch();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!isDirty) {
            reset(defaultFormValues);
        }
    }, [defaultFormValues, isDirty, reset]);

    const questionnaireProgress = useMemo(
        () => getQuestionnaireProgressState(formValues),
        [
            formValues.user_role,
            formValues.custom_role,
            formValues.business_description,
            formValues.lead_delivery_channels,
            formValues.crm_system,
            formValues.lead_delivery_other,
            formValues.tg_account,
            formValues.phone,
        ]
    );

    useEffect(() => {
        onQuestionnaireProgressChange?.(questionnaireProgress);
    }, [onQuestionnaireProgressChange, questionnaireProgress]);

    const { mutate: updateDescription, isLoading: isUpdateDescriptionLoading } = useMutation(
        async (data: IUserDescription) => {
            await UserSevice.updateDescription(data);
            queryClient.invalidateQueries('user');
        },
        {
            onSuccess: async () => {
                // Обновляем данные пользователя в store
                try {
                    const updatedUser = await UserSevice.getUser();
                    useUserStore.getState().setUserInfo(updatedUser);
                    reset(getFormDefaultValues(updatedUser));

                    // Проверяем, что шаг онбординга уже обновлен на бэкенде
                    // Бэкенд уже обновил шаг на 'choose_stream' при сохранении description
                    if (updatedUser.onboarding_step === 'choose_stream') {
                        // Шаг уже обновлен на бэкенде, просто вызываем onSuccess для перехода
                        // Не показываем уведомление и не вызываем updateBeginWorkStep
                        if (onSuccess) onSuccess({ skiped: false });
                        return;
                    }
                } catch {}

                // Если шаг не обновлен на бэкенде (не должно происходить, но на всякий случай)
                // Показываем уведомление и вызываем onSuccess
                enqueueSnackbar('Все данные успешно обновлены', { variant: 'success' });
                if (onSuccess) onSuccess({ skiped: false });
            },
            onError: () => {
                enqueueSnackbar('Ошибка при обновлении данных', { variant: 'error' });
            },
        }
    );

    const onSubmit = async (data: QuestionnaireFormValues) => {
        const roleValue = data.user_role === USER_ROLE_OTHER_VALUE ? data.custom_role : data.user_role;
        const deliveryChannels = data.lead_delivery_channels || [];

        updateDescription({
            tg_account: data.tg_account || '',
            business_description: data.business_description,
            first_name: data.first_name || '',
            user_role: roleValue,
            phone: data.phone,
            website_url: data.website_url,
            crm_system: deliveryChannels.includes('crm') ? data.crm_system : '',
            lead_delivery_channels: deliveryChannels,
            lead_delivery_other: deliveryChannels.includes('other') ? data.lead_delivery_other : '',
        });
    };

    const isLoading = isUpdateDescriptionLoading;
    const isDisabled = isLoading || !isDirty || !isValid;

    return (
        <LoadingWrapper isLoading={false}>
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box mb={1.5}>
                    <Title>{userInfo.isOnboardingCompleted ? 'Профиль' : 'Расскажите о своём бизнесе'}</Title>
                </Box>
                <Box
                    maxWidth="80%"
                    mb={20 / 8}
                >
                    <Typography
                        variant="body1"
                        color="text.primary"
                    >
                        {userInfo.isOnboardingCompleted
                            ? 'Контакты и общие настройки бизнеса.'
                            : 'Чтобы мы адаптировали сервис под ваши задачи, расскажите немного о себе и бизнесе'}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="first_name"
                                sx={{ fontWeight: 600 }}
                            >
                                Ваше имя
                            </Typography>
                            <Controller
                                name="first_name"
                                control={control}
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        value={field.value || ''}
                                        id="first_name"
                                        fullWidth
                                        placeholder="Введите ваше имя"
                                        error={!!errors.first_name}
                                        helperText={errors.first_name?.message}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <UserIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="user_role"
                                sx={{ fontWeight: 600 }}
                            >
                                Ваша деятельность
                            </Typography>
                            <Controller
                                name="user_role"
                                control={control}
                                render={({ field }) => (
                                    <FormControl
                                        fullWidth
                                        error={!!errors.user_role}
                                    >
                                        <Select
                                            {...field}
                                            id="user_role"
                                            displayEmpty
                                            sx={{
                                                borderRadius: '16px',
                                                backgroundColor: 'var(--lm-surface)',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'transparent',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--lm-text)',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'var(--lm-text)',
                                                },
                                                '& .MuiSelect-select': {
                                                    color: field.value ? 'var(--lm-text)' : 'var(--lm-muted)',
                                                },
                                                '&.Mui-focused .MuiSelect-select': {
                                                    color: 'var(--lm-text)',
                                                },
                                            }}
                                        >
                                            <MenuItem
                                                value=""
                                                disabled
                                            >
                                                Выберите вашу деятельность
                                            </MenuItem>
                                            {USER_ROLE_OPTIONS.map(({ value, label }) => (
                                                <MenuItem
                                                    key={value}
                                                    value={value}
                                                >
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.user_role && (
                                            <FormHelperText>{errors.user_role.message as string}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Stack>

                        {watch('user_role') === USER_ROLE_OTHER_VALUE && (
                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="custom_role"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Ваш вариант
                                </Typography>
                                <Controller
                                    name="custom_role"
                                    control={control}
                                    render={({ field }) => (
                                        <CustomTextField
                                            {...field}
                                            value={field.value || ''}
                                            id="custom_role"
                                            fullWidth
                                            placeholder="Введите вашу деятельность"
                                            error={!!errors.custom_role}
                                            helperText={errors.custom_role?.message}
                                            inputProps={{ maxLength: 40 }}
                                        />
                                    )}
                                />
                            </Stack>
                        )}

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="business_description"
                                sx={{ fontWeight: 600 }}
                            >
                                Опишите, что вы продаете и кому
                            </Typography>
                            <Controller
                                name="business_description"
                                control={control}
                                render={({ field }) => {
                                    const charCount = (field.value || '').length;
                                    const isError = charCount > BUSINESS_DESCRIPTION_MAX_CHARS;

                                    return (
                                        <>
                                            <CustomTextField
                                                {...field}
                                                value={field.value || ''}
                                                id="business_description"
                                                placeholder="Например: делаем ремонт квартир в Москве. Хотим находить собственников квартир, кому нужен ремонт в новостройке"
                                                fullWidth
                                                multiline
                                                rows={4}
                                                error={isError}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: '134px',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    },
                                                }}
                                            />

                                            <MaxChars
                                                isError={isError}
                                                charCount={charCount}
                                                maxChars={BUSINESS_DESCRIPTION_MAX_CHARS}
                                            />
                                        </>
                                    );
                                }}
                            />
                        </Stack>

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="lead_delivery_channels"
                                sx={{ fontWeight: 600 }}
                            >
                                Куда отправлять найденные лиды?
                            </Typography>
                            <Controller
                                name="lead_delivery_channels"
                                control={control}
                                render={({ field }) => {
                                    const selectedChannels = field.value || [];

                                    return (
                                        <FormControl error={!!errors.lead_delivery_channels}>
                                            <Stack
                                                gap={0.75}
                                                id="lead_delivery_channels"
                                            >
                                                {LEAD_DELIVERY_OPTIONS.map(({ value, label }) => {
                                                    const isChecked = selectedChannels.includes(value);

                                                    return (
                                                        <Stack
                                                            key={value}
                                                            gap={1}
                                                        >
                                                            <FormControlLabel
                                                                label={label}
                                                                control={
                                                                    <Checkbox
                                                                        checked={isChecked}
                                                                        onChange={(_, checked) => {
                                                                            const nextValue = checked
                                                                                ? [...selectedChannels, value]
                                                                                : selectedChannels.filter(
                                                                                      (channel: LeadDeliveryChannel) =>
                                                                                          channel !== value
                                                                                  );

                                                                            field.onChange(nextValue);
                                                                            if (!nextValue.includes('crm')) {
                                                                                clearErrors('crm_system');
                                                                            }
                                                                            if (!nextValue.includes('other')) {
                                                                                clearErrors('lead_delivery_other');
                                                                            }
                                                                            void trigger([
                                                                                'lead_delivery_channels',
                                                                                'crm_system',
                                                                                'lead_delivery_other',
                                                                            ]);
                                                                        }}
                                                                    />
                                                                }
                                                                sx={{
                                                                    m: 0,
                                                                    '& .MuiFormControlLabel-label': {
                                                                        fontSize: 15,
                                                                    },
                                                                }}
                                                            />

                                                            {value === 'crm' && isChecked && (
                                                                <Box ml={4}>
                                                                    <Controller
                                                                        name="crm_system"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <CustomTextField
                                                                                {...field}
                                                                                value={field.value || ''}
                                                                                id="crm_system"
                                                                                fullWidth
                                                                                placeholder="Например: Битрикс24, AmoCRM, Salesforce"
                                                                                error={!!errors.crm_system}
                                                                                helperText={errors.crm_system?.message}
                                                                                onChange={(event) => {
                                                                                    field.onChange(event);
                                                                                    void trigger([
                                                                                        'lead_delivery_channels',
                                                                                        'crm_system',
                                                                                    ]);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Box>
                                                            )}

                                                            {value === 'other' && isChecked && (
                                                                <Box ml={4}>
                                                                    <Controller
                                                                        name="lead_delivery_other"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <CustomTextField
                                                                                {...field}
                                                                                value={field.value || ''}
                                                                                id="lead_delivery_other"
                                                                                fullWidth
                                                                                placeholder="Например: WhatsApp, Slack или другому менеджеру"
                                                                                error={!!errors.lead_delivery_other}
                                                                                helperText={
                                                                                    errors.lead_delivery_other?.message
                                                                                }
                                                                                inputProps={{ maxLength: 150 }}
                                                                                onChange={(event) => {
                                                                                    field.onChange(event);
                                                                                    void trigger([
                                                                                        'lead_delivery_channels',
                                                                                        'lead_delivery_other',
                                                                                    ]);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    );
                                                })}
                                            </Stack>
                                            {errors.lead_delivery_channels && (
                                                <FormHelperText>
                                                    {errors.lead_delivery_channels.message as string}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    );
                                }}
                            />
                        </Stack>

                        <Stack gap={1}>
                            <Typography
                                component="label"
                                htmlFor="website_url"
                                sx={{ fontWeight: 600 }}
                            >
                                Ссылка на сайт (если есть)
                            </Typography>
                            <Controller
                                name="website_url"
                                control={control}
                                render={({ field }) => (
                                    <CustomInputField
                                        {...field}
                                        value={field.value || ''}
                                        placeholder="https://yoursite.ru"
                                        name="website_url"
                                        icon="link"
                                    />
                                )}
                            />
                        </Stack>

                        <Stack
                            gap={1.5}
                            mb={3}
                        >
                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="tg_account"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Ваш никнейм в Telegram
                                </Typography>
                                <Controller
                                    name="tg_account"
                                    control={control}
                                    render={({ field }) => (
                                        <CustomTextField
                                            {...field}
                                            value={field.value || ''}
                                            id="tg_account"
                                            placeholder="@username"
                                            fullWidth
                                            error={!!errors.tg_account}
                                            helperText={errors.tg_account?.message}
                                            onChange={(event) => {
                                                field.onChange(event);
                                                void trigger(['tg_account', 'phone']);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <TgArrowIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Stack>

                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="phone"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Номер телефона
                                </Typography>
                                <Controller
                                    name="phone"
                                    control={control}
                                    render={({ field }) => (
                                        <Box>
                                            <CustomTextField
                                                {...field}
                                                value={field.value || ''}
                                                id="phone"
                                                fullWidth
                                                placeholder="+7 (999) 123 45 67"
                                                error={!!errors.phone}
                                                onChange={(event) => {
                                                    field.onChange(event);
                                                    void trigger(['phone', 'tg_account']);
                                                }}
                                                InputProps={{
                                                    inputComponent: PhoneMaskField as any,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PhoneIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {errors.phone && (
                                                <Typography
                                                    variant="body2"
                                                    color="error"
                                                    sx={{ mt: 0.75 }}
                                                >
                                                    {errors.phone.message}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                />
                            </Stack>
                        </Stack>

                        {showTelegramOnboarding && !userInfo.isOnboardingCompleted && (
                            <TelegramOnboardingConnect initialConnected={!!userInfo.is_telegram_connected} />
                        )}

                        <Box
                            display="flex"
                            alignItems="flex-end"
                            flexGrow={1}
                            pb={1}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                fullWidth
                                type="submit"
                                disabled={isDisabled}
                            >
                                {isLoading ? <CircularProgress size={20} /> : 'Сохранить'}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Box>
        </LoadingWrapper>
    );
}
